import React, { useEffect, useRef, useState } from 'react';
import {
  FlexColumn,
  FormField,
  PasswordField,
  TextField,
  ToggleButton,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { Checkbox, FormControlLabel, ToggleButtonGroup } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { S3AuthMethod } from 'shared/types';
import { S3ActivityLogsIntegrationFormValues } from './activityLogsIntegrationUtils';
import FileSizeField from './FileSizeField';

const S3ActivityLogsForm = () => {
  const selectedAuthMethodRef = useRef<HTMLDivElement>(null);
  const methods = useFormContext<S3ActivityLogsIntegrationFormValues>();
  const {
    register,
    control,
    setValue,
    getValues,
    formState: { dirtyFields, errors },
  } = methods;

  const initialAuth: S3AuthMethod =
    getValues('properties.auth.auth_type') || 'access_key';
  const [authMethod, setAuthMethod] = useState<S3AuthMethod>(initialAuth);

  useEffect(() => {
    selectedAuthMethodRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [authMethod]);

  return (
    <FlexColumn sx={{ gap: 3 }}>
      <FormField title="Connection name" errorMessage={errors.name?.message}>
        <TextField
          {...register('name', { required: requiredLabel })}
          variant="outlined"
          size="large"
          placeholder="Connection name"
        />
      </FormField>

      <FormField
        title="Bucket name"
        errorMessage={errors.properties?.bucket_name?.message as string}
      >
        <TextField
          {...register('properties.bucket_name', { required: requiredLabel })}
          variant="outlined"
          size="large"
          placeholder="e.g. my-elementary-audit-logs"
        />
      </FormField>

      <FormField
        title="Region"
        errorMessage={errors.properties?.region?.message as string}
      >
        <TextField
          {...register('properties.region', { required: requiredLabel })}
          variant="outlined"
          size="large"
          placeholder="e.g. us-east-1"
        />
      </FormField>

      <FormField
        title="Max file size"
        optional
        errorMessage={errors.properties?.max_file_size?.message as string}
      >
        <Controller
          control={control}
          name="properties.max_file_size"
          render={({ field }) => (
            <FileSizeField
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
      </FormField>

      <FormField title="">
        <Controller
          control={control}
          name="properties.use_privatelink"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label="Connect via AWS PrivateLink"
            />
          )}
        />
      </FormField>

      <FormField title="Select authentication method">
        <ToggleButtonGroup
          fullWidth
          value={authMethod}
          onChange={(_, val: S3AuthMethod | null) => {
            const next = val || authMethod;
            setAuthMethod(next);
            setValue('properties.auth.auth_type', next, { shouldDirty: true });
          }}
          exclusive
        >
          <ToggleButton value="access_key" size="large">
            Secret access key
          </ToggleButton>
          <ToggleButton value="role" size="large">
            AWS role ARN
          </ToggleButton>
        </ToggleButtonGroup>
      </FormField>

      <FlexColumn gap="inherit" ref={selectedAuthMethodRef}>
        {authMethod === 'access_key' && (
          <>
            <FormField
              title="Access key ID"
              errorMessage={
                (
                  errors.properties?.auth as {
                    access_key_id?: { message?: string };
                  }
                )?.access_key_id?.message
              }
            >
              <PasswordField
                {...register('properties.auth.access_key_id', {
                  required: requiredLabel,
                })}
                size="large"
                isFormValueDirty={
                  (
                    dirtyFields.properties?.auth as {
                      access_key_id?: boolean;
                    }
                  )?.access_key_id
                }
                clearFormValue={() =>
                  setValue('properties.auth.access_key_id', '')
                }
                placeholder="e.g. AGDE221W4772DDQJUP4D9C"
              />
            </FormField>

            <FormField
              title="Secret access key"
              errorMessage={
                (
                  errors.properties?.auth as {
                    secret_access_key?: { message?: string };
                  }
                )?.secret_access_key?.message
              }
            >
              <PasswordField
                {...register('properties.auth.secret_access_key', {
                  required: requiredLabel,
                })}
                size="large"
                isFormValueDirty={
                  (
                    dirtyFields.properties?.auth as {
                      secret_access_key?: boolean;
                    }
                  )?.secret_access_key
                }
                clearFormValue={() =>
                  setValue('properties.auth.secret_access_key', '')
                }
                placeholder="Secret access key"
              />
            </FormField>
          </>
        )}

        {authMethod === 'role' && (
          <>
            <FormField
              title="AWS role ARN"
              errorMessage={
                (errors.properties?.auth as { role_arn?: { message?: string } })
                  ?.role_arn?.message
              }
            >
              <TextField
                {...register('properties.auth.role_arn', {
                  required: requiredLabel,
                })}
                variant="outlined"
                size="large"
                placeholder="e.g. arn:aws:iam::123456789012:role/my-role"
              />
            </FormField>

            <FormField
              title="External ID"
              optional
              errorMessage={
                (
                  errors.properties?.auth as {
                    external_id?: { message?: string };
                  }
                )?.external_id?.message
              }
            >
              <PasswordField
                {...register('properties.auth.external_id')}
                size="large"
                isFormValueDirty={
                  (
                    dirtyFields.properties?.auth as {
                      external_id?: boolean;
                    }
                  )?.external_id
                }
                clearFormValue={() =>
                  setValue('properties.auth.external_id', '')
                }
                placeholder="External ID"
              />
            </FormField>
          </>
        )}
      </FlexColumn>
    </FlexColumn>
  );
};

export default S3ActivityLogsForm;
